/**
 * Password phase of the step-up authentication modal.
 * Prompts the user to enter their password to verify identity.
 *
 * Presentational only — the parent modal owns the API call. Ported from
 * shipstation-lite/src/features/mfa/password-phase.tsx, widened with the
 * `phoneVerificationState` (G4) and `messageSet` (G2) copy variants from
 * ss-ui-client/src/global/step-up-authentication/phases/password/password-phase-body.jsx
 */

import { useEffect, useRef, useState } from 'react';
import { Anchor, Button, PasswordInput, Text, TextInput } from '@mantine/core';
import { FormattedMessage, useIntl } from 'react-intl';

import { MfaErrorBanner } from './mfa-error-banner';
import { ModalContent } from '../../components/modal-content';
import { ModalFooter } from '../../components/modal-footer';
import { PHONE_VERIFICATION_STATES } from '../mfa-constants';
import { mfaMessageSets } from '../mfa-messages';

import type { PhoneVerificationState } from '../mfa-constants';
import type { MfaMessageSet } from '../mfa-messages';
import type { ChangeEvent, FormEvent } from 'react';

export type PasswordPhaseProps = {
  username: string;
  passwordResetHref: string;
  isSubmitting: boolean;
  errorMessage?: string;
  /** Backend error code, rendered alongside `errorMessage`. */
  errorCode?: string;
  onSubmit: (password: string) => void;
  messageSet?: MfaMessageSet;
  phoneVerificationState?: PhoneVerificationState;
  isUnskippable?: boolean;
};

export const PasswordPhase = ({
  username,
  passwordResetHref,
  isSubmitting,
  errorMessage,
  errorCode,
  onSubmit,
  messageSet = mfaMessageSets.lastMfaClaimMessages,
  phoneVerificationState,
  isUnskippable = false,
}: PasswordPhaseProps) => {
  const intl = useIntl();
  const passwordRef = useRef<HTMLInputElement>(null);
  const [password, setPassword] = useState('');

  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting || !password) {
      return;
    }
    onSubmit(password);
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.currentTarget.value);
  };

  const getContextualMessage = () => {
    if (isUnskippable) {
      return messageSet.forcedMfaFlowContext;
    }
    if (phoneVerificationState === PHONE_VERIFICATION_STATES.MODAL) {
      return messageSet.secondSessionModalContext;
    }
    return null;
  };

  const contextualMessage = getContextualMessage();
  const passwordToggleButtonProps = { 'aria-label': intl.formatMessage(messageSet.togglePasswordAriaLabel) };

  return (
    <form onSubmit={handleFormSubmit}>
      <ModalContent>
        <Text fw={600} size='lg'>
          <FormattedMessage {...messageSet.passwordTitle} />
        </Text>

        {contextualMessage ? (
          <Text size='sm'>
            <FormattedMessage {...contextualMessage} />
          </Text>
        ) : null}

        <Text size='sm'>
          <FormattedMessage {...messageSet.passwordSubheader} />
        </Text>

        {errorMessage ? <MfaErrorBanner code={errorCode} message={errorMessage} /> : null}

        <TextInput disabled label={intl.formatMessage(messageSet.username)} value={username} />

        <PasswordInput
          ref={passwordRef}
          disabled={isSubmitting}
          label={intl.formatMessage(messageSet.password)}
          onChange={handlePasswordChange}
          value={password}
          visibilityToggleButtonProps={passwordToggleButtonProps}
        />

        <Anchor href={passwordResetHref} size='sm' target='_self'>
          <FormattedMessage {...messageSet.forgotPassword} />
        </Anchor>
      </ModalContent>

      <ModalFooter>
        <Button disabled={isSubmitting || !password} type='submit'>
          <FormattedMessage {...messageSet.submitButton} />
        </Button>
      </ModalFooter>
    </form>
  );
};
